import React, { useRef } from 'react';
import ContainerWithImage from '~/App/shared/components/ProductCard/components/ContainerWithImage';
import Title from '~/App/shared/components/ProductCard/components/Title';
import Financing from '~/App/shared/components/ProductCard/components/Financing';
import Auction from '~/App/shared/interfaces/Auction';
import { getAuctionLink } from '~/helpers/auction';
import Link from '~/App/shared/components/Link';
import useProductCardBaseProps from '~/App/shared/components/ProductCard/hooks/useProductCardBaseProps';
import Subtitle from '~/App/shared/components/ProductCard/components/Subtitle';
import Icons from '~/App/shared/components/ProductCard/components/Icons';
import Properties from '~/App/shared/components/ProductCard/components/Properties';
import { useTranslation } from '~/Locale';
import AuctionStatusForCard from '~/App/shared/components/ProductCard/components/AuctionStatusForCard';
import useAuctionStatuses from '~/App/shared/components/ProductCard/hooks/useAuctionStatuses';
import { getObjectPreviewProperties } from '~/helpers/processObject';
import {
  getObjectLocationData,
  isHeavyEquipmentObject
} from '~/App/views/ObjectPage/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { isRememberScrollActiveSelector } from '../../selectors/storeObjectsSelectors';
import {
  setFilterPageActiveAction,
  setFilterPageScrollData
} from '../../actions/storeObjectActions';
import { useScrollToProductCard } from '~/App/views/FilterPage/hooks/filterPageScrollHooks';
import { useLocation } from 'react-router';
import { Location } from './components/Location';
import { CardTag } from '~/App/shared/components/CardTag';
import { isEligibleForEvGrant } from '~/App/shared/helpers/evGrantEligibility';
import { hasExtraWinterTires } from '~/App/shared/helpers/winterTires';
import { SellIcon, Stack, TyresIcon } from '@kvdbil/components';
import Divider from '../Divider';
import useLocalization from '~/App/shared/hooks/useLocalization';

export interface ProductCardRegularProps {
  auction: Auction;
}

const ProductCardRegular = ({ auction }: ProductCardRegularProps) => {
  const { t } = useTranslation();
  const { domainLocale } = useLocalization();
  const isSwedishDomain = domainLocale === 'sv';
  const { imageProps, financingProps, processedAuction } =
    useProductCardBaseProps(auction);
  const isEvGrantEligible =
    isSwedishDomain && isEligibleForEvGrant(processedAuction);
  const showWinterTiresTag = hasExtraWinterTires(
    auction.processObject?.valuation
  );
  const auctionStatusItems = useAuctionStatuses(processedAuction, {
    processingPause: imageProps.isFixedPriceOnly
  });
  const isHeavyEquipment = isHeavyEquipmentObject(auction?.processObject);
  const numberOfBids = auction?.activeAuction?.bids?.length ?? 0;

  const properties = getObjectPreviewProperties(
    t,
    auction.processObject,
    false
  );

  const locationToDisplay = getObjectLocationData(auction.processObject).name;

  const dispatch = useDispatch();
  const ref = useRef<HTMLAnchorElement>(null);
  const isRememberScrollActive = useSelector(isRememberScrollActiveSelector);
  const location = useLocation();

  const handleClick = () => {
    if (isRememberScrollActive) {
      dispatch(setFilterPageActiveAction(false)); // Prevent scroll while navigating
      dispatch(
        setFilterPageScrollData({
          auctionId: auction.id,
          url: location.pathname
        })
      );
    }
  };

  useScrollToProductCard({ auctionId: auction.id, ref });

  return (
    <span ref={ref}>
      <Link
        to={getAuctionLink(auction)}
        onClick={handleClick}
        data-testid="product-card"
      >
        <ContainerWithImage
          {...imageProps}
          actionOpenedAt={auction.openedAt}
          objectLocation={locationToDisplay}
          isHeavyEquipment={isHeavyEquipment}
          numberOfBids={numberOfBids}
        >
          {auctionStatusItems.length !== 0 && (
            <AuctionStatusForCard items={auctionStatusItems} />
          )}
          <Icons auction={auction} />
          <Title>{auction.processObject.heading1}</Title>
          <Stack rowGap={{ initial: '0.25rem', tablet: 0 }}>
            <Subtitle>
              {auction.processObject.heading2 ??
                auction.processObject.heading3 ??
                ''}
            </Subtitle>
            <Properties properties={properties} />
            {locationToDisplay && (
              <Location locationToDisplay={locationToDisplay} />
            )}
          </Stack>
          {isEvGrantEligible && (
            <CardTag text={t('Eligible for EV grant')} icon={SellIcon} />
          )}
          {showWinterTiresTag && (
            <CardTag text={t('Winter tires included')} icon={TyresIcon} />
          )}
          <Divider height={0} />
          <Financing {...financingProps} />
        </ContainerWithImage>
      </Link>
    </span>
  );
};

export default ProductCardRegular;
